import { queryOptions } from "@tanstack/react-query"
import axios from "axios"
import { MINUTES_TO_MILLIS } from "lopermut"
import { DataResponseWithPagination, SegmentGroupsResponseDataSchema } from "schema"
import z from "zod"

type Response = DataResponseWithPagination<z.infer<typeof SegmentGroupsResponseDataSchema>>
type SegmentGroups = z.infer<typeof SegmentGroupsResponseDataSchema>

function getLocalStorageKey(companyId: string) {
  return `segment-groups-${companyId}`
}

function getLocalStorageSegmentGroups(companyId: string) {
  const segmentGroups = localStorage.getItem(getLocalStorageKey(companyId))
  if (!segmentGroups) return undefined

  const parseResult = SegmentGroupsResponseDataSchema.safeParse(JSON.parse(segmentGroups))

  if (parseResult.error) return undefined
  else return parseResult.data
}

function setLocalStorageSegmentGroups(
  companyId: string,
  data: z.infer<typeof SegmentGroupsResponseDataSchema>,
) {
  localStorage.setItem(getLocalStorageKey(companyId), JSON.stringify(data))
}

type Options = {
  onSuccess?: (data: SegmentGroups) => void
}

export function segmentGroupsQuery(companyId: string, options: Options = {}) {
  return queryOptions({
    queryKey: ["segment-groups", companyId],
    queryFn: async (): Promise<z.infer<typeof SegmentGroupsResponseDataSchema>> => {
      const response = await axios.get<Response>("/segments/groups")

      options.onSuccess?.(response.data.data)

      setLocalStorageSegmentGroups(companyId, response.data.data)

      return response.data.data
    },
    placeholderData: getLocalStorageSegmentGroups(companyId),
    staleTime: 5 * MINUTES_TO_MILLIS,
  })
}
